import React from 'react';
import {
  X,
  Sparkles,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  Wrench,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';
import { ReconciledItem } from '../../types/reconciliation';
import { generateAiMismatchAnalysis } from '../../engine/aiEngine';
import { StatusBadge } from '../common/Badge';

interface AiMismatchDrawerProps {
  item: ReconciledItem | null;
  onClose: () => void;
  onOpenBills?: (item: ReconciledItem) => void;
}

export const AiMismatchDrawer: React.FC<AiMismatchDrawerProps> = ({
  item,
  onClose,
  onOpenBills,
}) => {
  if (!item) return null;

  const analysis = generateAiMismatchAnalysis(item);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-slate-900/50 backdrop-blur-xs flex justify-end">
      <div className="w-full max-w-lg bg-white dark:bg-slate-900 h-full shadow-2xl border-l border-slate-200 dark:border-slate-800 flex flex-col justify-between overflow-hidden animate-slide-left">
        {/* Drawer Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-gradient-to-tr from-brand-600 to-sky-400 text-white shadow-md">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <span className="text-[10px] font-mono font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
                  AI Forensic Analysis
                </span>
                <h3 className="text-base font-bold text-slate-900 dark:text-white">
                  {item.paymentId}
                </h3>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-3 flex items-center gap-2">
            <StatusBadge status={item.status} size="sm" />
            <span className="text-xs text-slate-500 dark:text-slate-400">
              Customer: {item.customerName}
            </span>
          </div>
        </div>

        {/* Drawer Scrollable Content */}
        <div className="p-6 overflow-y-auto space-y-6 flex-1">
          {/* Classification Banner & Confidence Gauge */}
          <div className="p-4 rounded-xl bg-brand-50/70 dark:bg-brand-950/40 border border-brand-200 dark:border-brand-800 flex items-center justify-between">
            <div>
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                Classification
              </span>
              <div className="text-sm font-bold text-brand-900 dark:text-brand-200 mt-0.5">
                {analysis.category}
              </div>
            </div>
            <div className="text-right">
              <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                Confidence
              </span>
              <div className="text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400 mt-0.5">
                {analysis.confidenceScore}%
              </div>
            </div>
          </div>

          {/* Root Cause Diagnostic Summary */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
              Root Cause Diagnostic
            </h4>
            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-xs text-slate-800 dark:text-slate-200 leading-relaxed font-normal">
              {analysis.summary}
            </div>
          </div>

          {/* Financial Amounts Breakdown */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2">
              Technical Audit Concordance
            </h4>
            <div className="space-y-2">
              {analysis.technicalDetails.map((detail, idx) => (
                <div
                  key={idx}
                  className="p-3 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-500 mt-1.5 flex-shrink-0" />
                  <span>{detail}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Remediation Action Steps */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 mb-2 flex items-center gap-1.5">
              <Wrench className="w-3.5 h-3.5 text-amber-500" />
              Suggested Accounting Remediation
            </h4>
            <div className="space-y-2.5">
              {analysis.remediationSteps.map((step, idx) => (
                <div
                  key={idx}
                  className="p-3.5 rounded-xl border border-amber-200 dark:border-amber-900/60 bg-amber-50/50 dark:bg-amber-950/20 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-3"
                >
                  <div className="w-5 h-5 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 flex items-center justify-center font-bold text-[10px] flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">{step}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Drawer Footer */}
        <div className="p-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 flex items-center justify-between">
          {onOpenBills && item.billCount > 0 && (
            <button
              onClick={() => {
                onClose();
                onOpenBills(item);
              }}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-600 dark:text-brand-400 hover:underline"
            >
              <span>View {item.billCount} Linked ERP Bills</span>
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={onClose}
            className="ml-auto px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 text-white text-xs font-semibold shadow-sm transition"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};
